/**
 * useMyBookData Hook
 * Custom hook for fetching My Book data from real backend API
 *
 * TASK-281: Replaced mock data with real portfolio-service API calls
 * NO MOCK FALLBACK - errors propagate to UI for proper handling
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portfolioApiService, type DriftHeadline } from '../services/api/portfolioApiService';
import { PortfolioFilters } from '../types/portfolio-views';
import { useAccount } from '../contexts/AccountContext';

/**
 * My Book Data Structure
 * Aggregated view of all customers and their holdings
 */
export interface MyBookData {
  summary: MyBookSummary;
  topHoldings: TopHolding[];
  clients: MyBookClient[];
  alerts: MyBookAlert[];
}

export interface MyBookSummary {
  totalClients: number;
  totalPortfolios: number;
  totalAuM: number;
  avgClientAuM: number;
  avgHealth: number;
  needsAttention: number;
  needsRebalancing: number;

  // Performance metrics
  bookPerformanceYTD: number | null;
  bookPerformanceMTD: number | null;
  benchmarkYTD: number | null;
  alpha: number | null;
  benchmarkCode: string;
  benchmarkName: string;

  // Compliance metrics
  kycRenewalsDue: number;
  portfolioReviewsOverdue: number;
  concentrationAlerts: number;

  // Growth metrics
  aumGrowthQTD: number | null;
  newClientsQTD: number;
}

export interface TopHolding {
  isin: string;
  securityName: string;
  securityType: 'EQUITY' | 'BOND' | 'FUND' | 'CASH' | string;
  sector: string;
  currency: string;
  totalValue: number;
  percentOfBook: number;
  portfolioCount: number;
  clientCount: number;
}

export interface MyBookClient {
  customerId: string;
  customerName: string;
  referenceCurrency: string;
  riskProfile: string | null;
  mandateType: string | null;
  portfolioCount: number;
  totalAuM: number;
  performance: number | null;
  healthScore: number | null;
  status: 'healthy' | 'attention' | 'rebalance' | 'no_risk_profile';
  lastUpdate: string;
  driftStatus: string | null;
  /** TASK-875: the labelled drift figure, so an absence from the rebalance set is explainable
   *  from the row itself. */
  driftHeadline: DriftHeadline | null;
  kycRenewalDate?: string;
  lastPortfolioReview?: string;
  hasConcentrationAlert?: boolean;
}

export interface MyBookAlert {
  id: string;
  type: 'drift' | 'risk_profile' | 'kyc' | 'concentration' | 'review';
  severity: 'high' | 'medium' | 'low';
  clientId: string;
  clientName: string;
  message: string;
  actionRequired: string;
  createdAt: string;
}

interface UseMyBookDataReturn {
  data: MyBookData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// TASK-495: server-honoured book narrowing forwarded to /my-book/summary (Tier A).
// Kept separate from the cache-key `filters` so MyBookView's existing call is unchanged.
export interface MyBookServerFilters {
  status?: string[];
  minAum?: number;
  maxAum?: number;
}

/**
 * Fetch My Book data with filters applied
 *
 * @param filters - Portfolio filters from portfolioStore (cache key)
 * @param benchmark - Benchmark code (SPI or SMI), defaults to SPI
 * @param bookFilters - TASK-495 server-side book narrowing (status / AUM), forwarded
 *   to the backend and re-fetched on change. Omit for the legacy route behaviour.
 * @returns data, loading state, error state, and refetch function
 */
export function useMyBookData(
  filters: PortfolioFilters,
  benchmark: string = 'SPI',
  bookFilters?: MyBookServerFilters,
): UseMyBookDataReturn {
  const queryClient = useQueryClient();
  const { account } = useAccount();
  const accountId = account?.id ?? '';

  // Fetch summary and clients — include accountId in key so cache invalidates on account switch
  const summaryQuery = useQuery({
    queryKey: ['my-book-summary', accountId, filters, benchmark, bookFilters],
    queryFn: () => portfolioApiService.getMyBookSummary(benchmark, bookFilters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Fetch top holdings
  const holdingsQuery = useQuery({
    queryKey: ['my-book-top-holdings', accountId],
    queryFn: () => portfolioApiService.getMyBookTopHoldings(10),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Fetch alerts
  const alertsQuery = useQuery({
    queryKey: ['my-book-alerts', accountId],
    queryFn: () => portfolioApiService.getMyBookAlerts(),
    staleTime: 2 * 60 * 1000, // 2 minutes for alerts
    retry: 2,
  });

  // Combine loading states
  const loading = summaryQuery.isLoading || holdingsQuery.isLoading || alertsQuery.isLoading;

  // Combine error states - NO FALLBACK TO MOCK DATA
  const error = summaryQuery.error || holdingsQuery.error || alertsQuery.error
    ? 'Failed to load My Book data. Please check your connection and try again.'
    : null;

  // Transform API response to MyBookData structure
  const data: MyBookData | null = summaryQuery.data && holdingsQuery.data
    ? {
        summary: transformSummary(summaryQuery.data.summary),
        clients: transformClients(summaryQuery.data.clients),
        topHoldings: transformHoldings(holdingsQuery.data.top_holdings),
        alerts: transformAlerts(alertsQuery.data?.alerts ?? []),
      }
    : null;

  // Refetch function
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['my-book-summary'] });
    queryClient.invalidateQueries({ queryKey: ['my-book-top-holdings'] });
    queryClient.invalidateQueries({ queryKey: ['my-book-alerts'] });
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
}

/**
 * Transform backend summary to frontend format
 */
function transformSummary(apiSummary: Record<string, unknown>): MyBookSummary {
  return {
    totalClients: (apiSummary.total_clients as number) ?? 0,
    totalPortfolios: (apiSummary.total_portfolios as number) ?? 0,
    totalAuM: (apiSummary.total_aum as number) ?? 0,
    avgClientAuM: (apiSummary.average_portfolio_size as number) ?? 0,
    avgHealth: (apiSummary.avg_health as number) ?? 0,
    needsAttention: (apiSummary.clients_needing_attention as number) ?? 0,
    needsRebalancing: (apiSummary.clients_needing_rebalancing as number) ?? 0,
    // Performance - null if not available (NO DEFAULTS)
    bookPerformanceYTD: (apiSummary.book_performance_ytd as number) ?? null,
    bookPerformanceMTD: (apiSummary.book_performance_mtd as number) ?? null,
    benchmarkYTD: (apiSummary.benchmark_ytd as number) ?? null,
    alpha: (apiSummary.alpha as number) ?? null,
    benchmarkCode: (apiSummary.benchmark_code as string) ?? 'SPI',
    benchmarkName: (apiSummary.benchmark_name as string) ?? 'SPI 20',
    // Compliance
    kycRenewalsDue: (apiSummary.kyc_renewals_due as number) ?? 0,
    portfolioReviewsOverdue: (apiSummary.portfolio_reviews_overdue as number) ?? 0,
    concentrationAlerts: (apiSummary.concentration_alerts as number) ?? 0,
    // Growth
    aumGrowthQTD: (apiSummary.aum_growth_qtd as number) ?? null,
    newClientsQTD: (apiSummary.new_clients_qtd as number) ?? 0,
  };
}

/**
 * Transform backend clients to frontend format
 */
function transformClients(apiClients: Record<string, unknown>[]): MyBookClient[] {
  return (apiClients ?? []).map((client) => ({
    customerId: (client.client_id as string) ?? '',
    customerName: (client.client_name as string) ?? 'Unknown',
    referenceCurrency: (client.currency as string) ?? 'CHF',
    riskProfile: (client.risk_profile as string) ?? null,
    mandateType: (client.mandate_type as string) ?? null,
    portfolioCount: (client.portfolio_count as number) ?? 1,
    totalAuM: (client.total_value as number) ?? 0,
    performance: (client.performance as number) ?? null,
    healthScore: (client.health_score as number) ?? null,
    status: mapClientStatus(client.drift_status as string, client.status as string),
    lastUpdate: (client.last_update as string) ?? new Date().toISOString(),
    driftStatus: (client.drift_status as string) ?? null,
    // TASK-875: the labelled figure travels with the row, so a book list can explain why a
    // client is — or is not — in the rebalance set. `drift_percentage` reached here and was
    // dropped, which is why the list showed a status and no number at all.
    driftHeadline: (client.headline as DriftHeadline | null) ?? null,
    kycRenewalDate: client.kyc_renewal_date as string | undefined,
    lastPortfolioReview: client.last_portfolio_review as string | undefined,
    hasConcentrationAlert: (client.has_concentration_alert as boolean) ?? false,
  }));
}

/**
 * Map drift status to client status for UI
 */
function mapClientStatus(
  driftStatus: string | null,
  status: string | null
): 'healthy' | 'attention' | 'rebalance' | 'no_risk_profile' {
  if (driftStatus === 'no_risk_profile') return 'no_risk_profile';
  if (driftStatus === 'rebalance_needed') return 'rebalance';
  if (status === 'attention') return 'attention';
  return 'healthy';
}

/**
 * Transform backend holdings to frontend format
 * TASK-277: Handle backend field names and provide robust fallbacks
 */
function transformHoldings(apiHoldings: Record<string, unknown>[]): TopHolding[] {
  return (apiHoldings ?? []).map((holding) => {
    // Handle client_count: prefer explicit count, fallback to client_names array length
    const clientCount = (holding.client_count as number) ??
      ((holding.client_names as string[])?.length ?? 0);

    // Handle security_type: prefer explicit type, fallback to asset_class mapping
    const securityType = (holding.security_type as string) ??
      mapAssetClassToSecurityType(holding.asset_class as string);

    return {
      isin: (holding.isin as string) ?? '',
      securityName: (holding.security_name as string) ?? 'Unknown',
      securityType,
      sector: (holding.sector as string) ?? 'Other',
      currency: (holding.currency as string) ?? 'CHF',
      totalValue: (holding.total_value as number) ?? 0,
      percentOfBook: (holding.percent_of_book as number) ?? 0,
      portfolioCount: (holding.portfolio_count as number) ?? 0,
      clientCount,
    };
  });
}

/**
 * Map asset_class to securityType for frontend display.
 *
 * A fallback only — the backend supplies `security_type` and this fires when it does not. It
 * is a hand-kept copy of `portfolio_repository.get_aggregated_holdings`' map and must stay in
 * step with it: TASK-878 corrected the backend (`Liquidity` was missing, so a CHF cash account
 * was typed EQUITY on the book page) and this copy was left behind, which is how the two come
 * to disagree about the same holding.
 */
function mapAssetClassToSecurityType(assetClass: string | undefined): string {
  // Absent is not equity. Defaulting to the largest, most consequential type is the
  // absence-as-a-value defect (TASK-872) with a security type instead of a number.
  if (!assetClass) return 'OTHER';

  const mapping: Record<string, string> = {
    'Equities': 'EQUITY',
    'Equity': 'EQUITY',
    'Bonds': 'BOND',
    'Bond': 'BOND',
    'Fixed Income': 'BOND',
    'Cash': 'CASH',
    'Money Market': 'CASH',
    // TASK-878: the CANONICAL asset class (`taxonomy.ASSET_CLASSES`). Without it,
    // "Swiss Bank (CHF Cash / Kontokorrent)" typed as EQUITY.
    'Liquidity': 'CASH',
    'Alternatives': 'FUND',
    'Real Estate': 'FUND',
    'Commodities': 'FUND',
  };

  return mapping[assetClass] ?? 'OTHER';
}

/**
 * Transform backend alerts to frontend format
 */
function transformAlerts(apiAlerts: Record<string, unknown>[]): MyBookAlert[] {
  return (apiAlerts ?? []).map((alert, index) => {
    // Map API alert type to frontend type
    const typeMapping: Record<string, 'drift' | 'risk_profile' | 'kyc' | 'concentration' | 'review'> = {
      'missing_risk_profile': 'risk_profile',
      'rebalancing_needed': 'drift',
      'kyc_renewal': 'kyc',
      'concentration_alert': 'concentration',
      'review_overdue': 'review',
    };

    const alertType = (alert.type as string) ?? '';
    const mappedType = typeMapping[alertType] ?? 'drift';

    return {
      id: `alert-${index}-${Date.now()}`,
      type: mappedType,
      severity: (alert.severity as 'high' | 'medium' | 'low') ?? 'medium',
      clientId: (alert.client_id as string) ?? '',
      clientName: (alert.client_name as string) ?? 'Unknown',
      message: (alert.message as string) ?? '',
      actionRequired: (alert.action as string) ?? 'Review required',
      createdAt: new Date().toISOString(),
    };
  });
}
